import { useCallback, useMemo, useState } from 'react'
import { Tag, Popover, Row, Col, Divider } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import {
  resetEmptyColumns,
  resetNegateColumns,
  removeFilter,
  updateEmptyColumn,
  removeNegateColumnsAndAssociatedFilters,
  addPriceFilter,
  resetDynamicLabelToInitial,
  updateMultipleSearchFilters,
  removeFilterTags,
} from '../../../Slices/Kiosk/TableOptionsSlice'
import { populateTableWithArticles } from '../../../Services/Kiosk/ArticleServices'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  emptyPopover,
  mktFilterYes,
  negatePopover,
  searchedPopover,
  TableColumnTranslationPrefix,
} from '../../../Constants/Configuration'
import { renderTranslation } from '../../../Helpers/GenericHelpers'
import { setCapitalLetter } from '../../../Helpers/StringHelpers'
import { getSearchedTableFiltersValue, retrieveActiveEmptyColumns } from '../../../Helpers/FilterHelper'
import { populateSubRuleTableWithArticles } from '../../../Services/Omnichannel/SeasonRuleService'
import {
  removeSubRuleFilter,
  removeSubRuleNegateColumnsAndAssociatedFilters,
  updateSubRuleEmptyColumn,
  resetNegateColumns as resetSubruleNegateColumns,
  resetEmptyColumns as resetSubruleEmptyColumns,
  updateMultipleSearchFilters as updateSubruleMultipleSearchFilters,
  removeSubruleFilterTags,
} from '../../../Slices/Omnichannel/SubRuleSlice'
import { ArticleColumn, ArticleDefaultColumn } from '../../../Constants/TableConfiguration'
import { useAppDispatch, useAppSelector } from '../../../hooks'

function TagFilters(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)

  const tableColumns = props.tableColumns
  const emptyTableColumns = props.emptyTableColumns
  const emptyExtraDescriptions = props.emptyExtraDescriptions
  const negateTableColumns = props.negateTableColumns
  const tableFilters = props.tableFilters
  const priceFilter = props.priceFilter

  const defaultCols: ArticleDefaultColumn[] = props.defaultCols
  const allMarketplace = props.allMarketplace

  const [emptyVisible, setEmptyVisible] = useState(false)
  const [negateVisible, setNegateVisible] = useState(false)

  const emptyColumns = useMemo(() => {
    const empty = retrieveActiveEmptyColumns(emptyTableColumns, emptyExtraDescriptions)
    return empty
  }, [emptyTableColumns, emptyExtraDescriptions])

  const searchedColumns = useMemo(() => {
    const filterSearched = getSearchedTableFiltersValue(
      tableColumns,
      tableFilters,
      negateTableColumns,
      priceFilter,
      allMarketplace,
    )
    return filterSearched
  }, [tableColumns, tableFilters, negateTableColumns, priceFilter, allMarketplace])

  const startArticleSearch = useCallback(() => {
    if (props.isFromDiWa) {
      dispatch(populateTableWithArticles())
    } else {
      dispatch(populateSubRuleTableWithArticles(false))
    }
  }, [dispatch, props.isFromDiWa])

  const filterRemove = useCallback(
    element => {
      if (props.isFromDiWa) {
        dispatch(removeFilter(element))
      } else {
        dispatch(removeSubRuleFilter(element))
      }
    },
    [dispatch, props.isFromDiWa],
  )

  const filterPriceRemove = useCallback(() => {
    dispatch(addPriceFilter({}))
    startArticleSearch()
  }, [dispatch, startArticleSearch])

  const negateColumnRemove = useCallback(
    element => {
      if (props.isFromDiWa) {
        dispatch(removeNegateColumnsAndAssociatedFilters(element))
      } else {
        dispatch(removeSubRuleNegateColumnsAndAssociatedFilters(element))
      }
    },
    [dispatch, props.isFromDiWa],
  )

  const emptyColumnUpdate = useCallback(
    element => {
      if (props.isFromDiWa) {
        dispatch(updateEmptyColumn({ value: element, active: false }))
      } else {
        dispatch(updateSubRuleEmptyColumn({ value: element, active: false }))
      }
      startArticleSearch()
    },
    [dispatch, props.isFromDiWa, startArticleSearch],
  )

  const emptyColumnLangDescriptionUpdate = useCallback(
    element => {
      const objectToDispatch = { label: element.field, specialLanguageFilter: true, languageCode: element.languageCode }
      dispatch(resetDynamicLabelToInitial(objectToDispatch))
      startArticleSearch()
    },
    [dispatch, startArticleSearch],
  )

  const handlePopoverClose = useCallback(popoverId => {
    if (popoverId === emptyPopover) {
      setEmptyVisible(false)
    }
    if (popoverId === negatePopover) {
      setNegateVisible(false)
    }
  }, [])

  const renderPopoverClose = useCallback(
    popoverId => {
      return (
        <CloseOutlined
          className='tag-filter-close-icon'
          onClick={e => {
            e.stopPropagation()
            handlePopoverClose(popoverId)
          }}
        />
      )
    },
    [handlePopoverClose],
  )

  const updateMultipleSearchFilter = useCallback(
    (filterName, searchedValue) => {
      if (props.isFromDiWa) {
        dispatch(updateMultipleSearchFilters({ key: filterName, value: searchedValue }))
        if(filterName === ArticleColumn.Tag) {
          dispatch(removeFilterTags(searchedValue))
        }
      } else {
        dispatch(updateSubruleMultipleSearchFilters({ key: filterName, value: searchedValue }))
        if(filterName === ArticleColumn.Tag) {
          dispatch(removeSubruleFilterTags(searchedValue))
        }
      }
    },
    [dispatch, props.isFromDiWa],
  )

  const excludedContent = useMemo(
    () => (
      <Row>
        {renderPopoverClose(emptyPopover)}
        {emptyColumns.map(element => {
          return element.field ? (
            props.isFromDiWa ? (
              <Col key={'empty-' + element.field} span={24}>
                <CloseOutlined
                  className='close-icon'
                  onClick={e => {
                    e.stopPropagation()
                    emptyColumnLangDescriptionUpdate(element)
                    if (emptyColumns.length === 1) {
                      handlePopoverClose(emptyPopover)
                    }
                  }}
                />
                {renderTranslation(
                  language,
                  TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(element.field)],
                ) +
                  ' - ' +
                  element.languageCode}
              </Col>
            ) : null
          ) : (
            <Col key={'empty-' + element} span={24}>
              <CloseOutlined
                className='close-icon'
                onClick={e => {
                  e.stopPropagation()
                  emptyColumnUpdate(element)
                  if (emptyColumns.length === 1) {
                    handlePopoverClose(emptyPopover)
                  }
                }}
              />
              <span>
                {renderTranslation(language, TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(element)])}
              </span>
            </Col>
          )
        })}
      </Row>
    ),
    [
      renderPopoverClose,
      emptyColumns,
      props.isFromDiWa,
      language,
      emptyColumnLangDescriptionUpdate,
      handlePopoverClose,
      emptyColumnUpdate,
    ],
  )
  const negateContent = useMemo(() => {
    const filters = []

    negateTableColumns.forEach(element => {
      const label = defaultCols.filter(col => col.key && col.key === element)

      const multipleSearch = defaultCols.find(col => col.name === element)?.multipleSearch

      if (multipleSearch) {
        const elements = tableFilters[element] ?? ''
        const searchedValues = elements.split('|')
        searchedValues.forEach((searchedValue, searchedIndex) => {
          filters.push(
            <Col key={'negate-' + element + '-' + searchedValue} span={24}>
              <CloseOutlined
                className='close-icon'
                onClick={e => {
                  e.stopPropagation()
                  updateMultipleSearchFilter(element, searchedValue)
                  if (searchedValues.length === 1) {
                    negateColumnRemove(element)
                  }
                  if (negateTableColumns.length === 1) {
                    handlePopoverClose(negatePopover)
                  }
                  startArticleSearch()
                }}
              />
              <span>
                {renderTranslation(
                  language,
                  TranslationKeys[
                    TableColumnTranslationPrefix + setCapitalLetter(label.length ? label[0].name : element)
                  ],
                ) +
                  ' ' +
                  searchedValue}
              </span>
            </Col>,
          )
        })

        return
      }

      filters.push(
        <Col key={'negate-' + element} span={24}>
          <CloseOutlined
            className='close-icon'
            onClick={e => {
              e.stopPropagation()
              negateColumnRemove(element)
              if (negateTableColumns.length === 1) {
                handlePopoverClose(negatePopover)
              }
              startArticleSearch()
            }}
          />
          <span>
            {tableFilters[element]
              ? renderTranslation(
                  language,
                  TranslationKeys[
                    TableColumnTranslationPrefix + setCapitalLetter(label.length ? label[0].name : element)
                  ],
                ) +
                ' ' +
                tableFilters[element]
              : renderTranslation(
                  language,
                  TranslationKeys[
                    TableColumnTranslationPrefix + setCapitalLetter(label.length ? label[0].name : element)
                  ],
                )}
          </span>
        </Col>,
      )
    })

    return (
      <Row>
        {renderPopoverClose(negatePopover)}
        {filters}
      </Row>
    )
  }, [
    negateTableColumns,
    renderPopoverClose,
    defaultCols,
    tableFilters,
    language,
    updateMultipleSearchFilter,
    startArticleSearch,
    negateColumnRemove,
    filterRemove,
    handlePopoverClose,
  ])

  const searchedContent = useMemo(() => {
    const filters = []

    searchedColumns.forEach((element, index) => {
      if (element.field && props.isFromDiWa) {
        const column = defaultCols.find(col => col.key && col.key === element.field)
        filters.push(
          <Tag
            key={'searched-' + index + '-' + element.field}
            className='searched-tag'
            onClose={e => {
              handlePopoverClose(searchedPopover)
              filterPriceRemove()
            }}
            closable
          >
            {renderTranslation(
              language,
              TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(column ? column.name : element.field)],
            ) +
              ' ' +
              element.value}
          </Tag>,
        )

        return
      }

      const multipleSearch = defaultCols.find(col => col.name === element)?.multipleSearch

      if (multipleSearch) {
        const searchedValues = tableFilters[element].split('|')
        searchedValues.forEach((searchedValue, searchedIndex) => {
          filters.push(
            <Tag
              className={'searched-tag ' + element}
              onClose={e => {
                updateMultipleSearchFilter(element, searchedValue)

                if (searchedValues.length === 1) {
                  filterRemove(element)
                }
                startArticleSearch()
              }}
              closable={props.isFromDiWa || element !== ArticleColumn.Season}
              key={'search-tag-' + searchedIndex + '-' + element + '-' + searchedValue}
            >
              {searchedValue}
            </Tag>,
          )
        })

        return
      }

      const mkt = allMarketplace.find(
        x =>
          (x.column && x.column.toLowerCase() === element.toLowerCase()) ||
          (x.name && x.name.toLowerCase() === element.toLowerCase()),
      )

      filters.push(
        <Tag
          className='searched-tag'
          onClose={e => {
            filterRemove(element)
            startArticleSearch()
          }}
          closable={props.isFromDiWa || element !== ArticleColumn.Season}
          key={'search-tag-' + index + '-' + element}
        >
          {mkt
            ? (mkt.alias ? mkt.alias : mkt.name) +
              ' ' +
              (tableFilters[element] === mktFilterYes
                ? renderTranslation(language, TranslationKeys.tableMktYes)
                : renderTranslation(language, TranslationKeys.tableMktNo))
            : tableFilters[element]}
        </Tag>,
      )
    })

    return <Row>{filters}</Row>
  }, [
    searchedColumns,
    props.isFromDiWa,
    defaultCols,
    allMarketplace,
    tableFilters,
    language,
    handlePopoverClose,
    filterPriceRemove,
    updateMultipleSearchFilter,
    startArticleSearch,
    filterRemove,
  ])

  const negateFilterCount = useMemo(() => {
    const multipleSearchs =
      negateTableColumns?.filter((x: string) => defaultCols.find((y: any) => y.name === x && y.multipleSearch)) ?? []

    let multiSearchCount = 0

    multipleSearchs.forEach((element: string) => {
      if (tableFilters[element]) {
        const searched = tableFilters[element].split('|')
        multiSearchCount = multiSearchCount + searched.length
      }
    })

    return negateTableColumns.length + multiSearchCount - multipleSearchs.length
  }, [defaultCols, negateTableColumns, tableFilters])

  return (
    <div className='manual-filter-container'>
      {emptyColumns.length !== 0 ? (
        <Popover
          content={excludedContent}
          open={emptyVisible}
          onOpenChange={setEmptyVisible}
          trigger='click'
          id={emptyPopover}
        >
          <Tag
            className='empty-tag'
            onClose={e => {
              handlePopoverClose(emptyPopover)
              if (props.isFromDiWa) {
                dispatch(resetEmptyColumns())
              } else {
                dispatch(resetSubruleEmptyColumns())
              }
              startArticleSearch()
            }}
            closable
          >
            <span>{emptyColumns.length}</span>
            <Divider className='tag-divider' type='vertical' />
            {renderTranslation(language, TranslationKeys.emptyColumnsTag)}
          </Tag>
        </Popover>
      ) : null}
      {negateTableColumns.length !== 0 && negateTableColumns.find(el => tableFilters[el]) ? (
        <Popover
          content={negateContent}
          open={negateVisible}
          onOpenChange={setNegateVisible}
          trigger='click'
          id={negatePopover}
        >
          <Tag
            className='negate-tag'
            onClose={e => {
              handlePopoverClose(negatePopover)
              if (props.isFromDiWa) {
                dispatch(resetNegateColumns())
              } else {
                dispatch(resetSubruleNegateColumns())
              }
              startArticleSearch()
            }}
            closable
          >
            <span>{negateFilterCount}</span>
            <Divider className='tag-divider' type='vertical' />
            {renderTranslation(language, TranslationKeys.negateColumnsTag)}
          </Tag>
        </Popover>
      ) : null}
      {searchedContent}
    </div>
  )
}

export default TagFilters
